// Reading the parts of `preview` a change-detail screen shows: the per-row ledger a packet leaves
// behind, and the value the platform was read back for.
//
// Both are read from `preview` rather than from the audit tail: the events are the immutable
// trace, `preview` is the readable state, and only the second one answers "what does the platform
// hold now". Pure — the adapter calls it once per request, so no component parses a preview.
import type {
  PlatformActionLedger,
  PlatformActionLedgerKey,
  PlatformActionLedgerRow,
  PlatformActionValues,
} from '../models/platformAction.types'
import type { ActionPreviewDto } from '../models/platformAction.wire'

/** The four keys a ledger can arrive under, in the order they are looked for. */
const LEDGER_KEYS: readonly PlatformActionLedgerKey[] = [
  'packet_items',
  'items',
  'created_children',
  'execution_steps',
]

/** Verdicts that mean the platform ended up holding the row. */
const LANDED = new Set(['applied', 'reused', 'duplicate'])

/** Verdicts that mean it certainly did not. Everything else is genuinely unknown. */
const REFUSED = new Set(['not_applied', 'failed', 'not_attempted'])

export function isLanded(verdict: string): boolean {
  return LANDED.has(verdict)
}

export function isRefused(verdict: string): boolean {
  return REFUSED.has(verdict)
}

export function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

function text(value: unknown): string {
  if (value === null || value === undefined) return ''
  return typeof value === 'string' ? value : String(value)
}

function rowLabel(key: PlatformActionLedgerKey, row: Record<string, unknown>): string {
  if (key === 'execution_steps') return text(row.action) || text(row.target_id)
  if (key === 'created_children') return text(row.child_id) || text(row.source_id)
  // `items` plans an entity by name; `packet_items` names the thing it wrote to.
  return text(row.name) || text(row.target_id) || text(row.action_type)
}

function normalizeRow(
  key: PlatformActionLedgerKey,
  raw: unknown,
  position: number,
): PlatformActionLedgerRow {
  const row = asRecord(raw)
  const index = typeof row.index === 'number' ? row.index : position
  return {
    index,
    label: rowLabel(key, row),
    verdict: text(row.verdict),
    reason: text(row.reason),
    code: text(row.provider_code),
  }
}

/**
 * The row ledger, or `null` where the request left none.
 *
 * Which key carries it depends on what the action did — a non-create packet writes
 * `packet_items`, a bulk create `items`, a tree create `created_children`, a composed mutate
 * `execution_steps` — so the key is discovered, never assumed.
 */
export function readLedger(
  preview: ActionPreviewDto | null | undefined,
): PlatformActionLedger | null {
  if (!preview || typeof preview !== 'object') return null
  for (const key of LEDGER_KEYS) {
    const raw = preview[key]
    if (!Array.isArray(raw) || raw.length === 0) continue
    const rows = raw.map((value, position) => normalizeRow(key, value, position))
    return {
      key,
      rows,
      landed: rows.filter((row) => isLanded(row.verdict)).length,
      total: rows.length,
    }
  }
  return null
}

/**
 * The before / asked-for / read-back values, plus which of the three read-back answers applies.
 * `verified_at` is a clock: it says a read-back AGREED, never what the platform holds.
 */
export function readValues(
  preview: ActionPreviewDto | null | undefined,
  verifiedAt: string | null,
): PlatformActionValues {
  const source = preview && typeof preview === 'object' ? preview : null
  const ran = source !== null && Object.hasOwn(source, 'verified')
  return {
    current: asRecord(source?.current),
    requested: asRecord(source?.requested),
    verified: ran ? asRecord(source.verified) : null,
    readBack: !ran ? 'unchecked' : verifiedAt ? 'verified' : 'unconfirmed',
  }
}

/**
 * Fields the platform held that were reviewed alongside the change but never asked to move —
 * read-only context, collected into one line instead of diff rows that would each read "—".
 */
export function contextEntries(values: PlatformActionValues): [string, unknown][] {
  return Object.entries(values.current).filter(([key]) => !Object.hasOwn(values.requested, key))
}

/** The keys a change is actually about — what it asked to move, and nothing else. */
export function changedKeys(values: PlatformActionValues): string[] {
  return Object.keys(values.requested)
}
